import type { OrgInvitation } from 'pure-js/types/OrgInvitation'
import { OrgInvitationStatus } from 'pure-js/types/OrgInvitation'
import type { OrgMemberRow } from 'pure-js/types/OrganizationMembership'

function normalize(q: string): string {
  return q.trim().toLowerCase()
}

/**
 * Filters members by case-insensitive substring match against displayName or
 * email. Returns the input unchanged when the query is empty / whitespace.
 */
export function filterMembersByQuery(members: OrgMemberRow[], rawQuery: string): OrgMemberRow[] {
  const q = normalize(rawQuery)
  if (!q) return members
  return members.filter(
    (m) => (m.displayName ?? '').toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q)
  )
}

/**
 * Filters invitations by case-insensitive substring match against email.
 * Returns the input unchanged when the query is empty.
 */
export function filterInvitesByEmail(invites: OrgInvitation[], rawQuery: string): OrgInvitation[] {
  const q = normalize(rawQuery)
  if (!q) return invites
  return invites.filter((i) => i.email.toLowerCase().includes(q))
}

/** Sorts invitations newest-first by `acceptedAt` (accepted) else `createdAt`. Pure. */
export function sortInvitesByRecency(invites: OrgInvitation[]): OrgInvitation[] {
  const timestamp = (i: OrgInvitation): number => {
    const raw = i.acceptedAt ?? i.createdAt
    const parsed = raw ? new Date(raw).getTime() : 0
    return Number.isNaN(parsed) ? 0 : parsed
  }
  return [...invites].sort((a, b) => timestamp(b) - timestamp(a))
}

/**
 * Counts invitations grouped by status. Always returns all four status keys,
 * defaulting to 0 when there are no rows of that status.
 */
export function countInvitesByStatus(invites: OrgInvitation[]): Record<OrgInvitationStatus, number> {
  const counts: Record<OrgInvitationStatus, number> = {
    [OrgInvitationStatus.PENDING]: 0,
    [OrgInvitationStatus.ACCEPTED]: 0,
    [OrgInvitationStatus.EXPIRED]: 0,
    [OrgInvitationStatus.REVOKED]: 0
  }
  for (const inv of invites) counts[inv.status] += 1
  return counts
}
